// Steam over REST (server steam.ts, http.ts), for Settings > Account and the sign-in screen:
// - the account's link and starting a link (the page goes to Steam and comes back with #steamLinked or
//   #steamLinkError); a link is permanent;
// - the web sign-in's address (/api/auth/steam, ending in #sso or #ssoError like the Hep.gg login, so the existing
//   hand-back reads it once markSsoStarted was called);
// - the desktop app's sign-in: the shell (apps/desktop preload) exposes window.gcDesktop.steamTicket(), a Web API
//   session ticket from the Steam client, which the server checks with Steam and answers with a session token.
// The web session's token rides in the Authorization header.

import { loadToken, markSsoStarted, saveToken, type AccountInfo } from './auth';

export interface SteamLinkInfo {
  steamId: string;
  persona: string | null;
  avatar: string | null;
  linkedAt: number;
}

async function call<T>(method: 'GET' | 'POST', path: string, body?: unknown): Promise<T> {
  const t = loadToken();
  const headers: Record<string, string> = t ? { authorization: `Bearer ${t}` } : {};
  if (body !== undefined) headers['content-type'] = 'application/json';
  const r = await fetch(path, { method, headers, body: body === undefined ? undefined : JSON.stringify(body) });
  const j = (await r.json().catch(() => ({}))) as T & { error?: string };
  if (!r.ok) throw new Error(j.error ?? `Request failed (${r.status})`);
  return j;
}

/** Whether the server offers Steam sign-in at all (a key configured and steam_signin above Off). */
export async function steamOffered(): Promise<boolean> {
  try {
    const r = await fetch('/api/auth/config');
    return !!((await r.json()) as { steam?: boolean }).steam;
  } catch {
    return false;
  }
}

/** The account's Steam link; `available` false while linking Steam (steam_link) is off for this account. */
export function steamStatus(): Promise<{ available: boolean; link: SteamLinkInfo | null }> {
  return call('GET', '/api/auth/steam/status');
}

const LINK_STARTED = 'gc.steamLinkStarted';

/** Open Steam to link the signed-in account; the page comes back to this site. */
export async function startSteamLink(): Promise<void> {
  const { url } = await call<{ url: string }>('POST', '/api/auth/steam/link');
  try {
    sessionStorage.setItem(LINK_STARTED, String(Date.now()));
  } catch {
    /* private mode: the result still shows on the account's row */
  }
  location.assign(url);
}

/** The web sign-in with Steam: the address the sign-in screen's Steam button opens (call this from its click first). */
export function startSteamSignIn(ref?: string): string {
  markSsoStarted();
  return `/api/auth/steam${ref ? `?ref=${encodeURIComponent(ref)}` : ''}`;
}

/** One Discord client the shell reaches: its build, the signed-in user's name (on this machine only) and the account match */
export interface DesktopDiscordClient {
  build: string;
  name: string;
  account: 'linked' | 'other' | 'unknown';
  shown: 'shown' | 'refused' | 'none';
  plain: boolean;
  refusal: string;
  other: boolean;
}

export interface DesktopDiscordState {
  state: 'showing' | 'ready' | 'connecting' | 'not-running' | 'error' | 'off';
  message: string;
  at: number;
  clients?: DesktopDiscordClient[];
  lines?: string[];
}

export interface DesktopBridge {
  /** Whether the Steam client API is up in this run */
  steam: boolean;
  /** The machine's memory in GB (engine/desktop.ts reads it for the desktop budgets) */
  memoryGB?: number;
  /** A hex Web API session ticket from the Steam client (GetAuthTicketForWebApi), or null outside Steam */
  steamTicket(): Promise<string | null>;
  /** The player's activity for Discord and Steam presence (ui/desktopPresence.ts), or null to clear it */
  presence(report: { scene: string | null; display: string | null; era: string | null; genW: number | null } | null): void;
  /** The Steam overlay on one of its dialogs; false when the overlay is not up */
  overlayDialog?(kind: 'achievements' | 'friends' | 'store'): Promise<boolean>;
  /** Whether the page draws frames now (the shell repaints the window for the Steam overlay while it does not) */
  drawing?(on: boolean): void;
  /**
   * Discord presence's state in the shell; newer shells add the connected clients and Settings' lines (where the
   * activity shows and as whom, or why not)
   */
  discord?(): Promise<DesktopDiscordState | null>;
  /** The account's linked Discord id ('' with none linked); the shell compares the Discord it reaches with it (absent in older shells) */
  discordAccount?(id: string): void;
  /** The window's frame state now and on each change; returns the unsubscribe */
  onFrame?(fn: (st: { fullscreen: boolean; maximized: boolean }) => void): () => void;
  /** The player's answers in the Steam overlay to in-game purchases; returns a function that stops listening (absent in older shells) */
  onMicroTxn?(handler: (answer: { orderId: string; authorized: boolean }) => void): () => void;
}

/** The desktop shell's bridge, when this page runs inside it. */
export function desktopBridge(): DesktopBridge | null {
  const b = (globalThis as { gcDesktop?: DesktopBridge }).gcDesktop;
  return b && typeof b.steamTicket === 'function' ? b : null;
}

/**
 * The desktop app's sign-in: a ticket from the Steam client checked by the server. Saves and returns the session
 * token (the caller starts the session with it); throws the server's refusal in its words.
 */
export async function steamTicketSignIn(): Promise<{ token: string; account: AccountInfo; created: boolean }> {
  const bridge = desktopBridge();
  if (!bridge) throw new Error('Steam sign-in needs the GridController app from Steam.');
  const ticket = await bridge.steamTicket();
  if (!ticket) throw new Error('Steam is not running. Start GridController from Steam.');
  const r = await call<{ token: string; account: AccountInfo; created: boolean }>('POST', '/api/auth/steam/ticket', { ticket });
  saveToken(r.token);
  return r;
}

/** A link's result handed back in the address, read once when this module loads (the address is cleaned). */
function readLinkHash(): { ok: boolean; error?: string } | null {
  if (typeof location === 'undefined') return null;
  const p = new URLSearchParams(location.hash.slice(1));
  const ok = p.get('steamLinked');
  const error = p.get('steamLinkError');
  if (!ok && !error) return null;
  history.replaceState(null, '', location.pathname + location.search);
  let started = false;
  try {
    started = !!sessionStorage.getItem(LINK_STARTED);
    sessionStorage.removeItem(LINK_STARTED);
  } catch {
    /* no session storage */
  }
  if (!started) return null;
  return ok ? { ok: true } : { ok: false, error: error ?? 'Steam linking failed.' };
}

let pending = readLinkHash();

/** The result of a link this tab started, once (Settings shows it). */
export function takeSteamLinkResult(): { ok: boolean; error?: string } | null {
  const r = pending;
  pending = null;
  return r;
}

export function peekSteamLinkResult(): boolean {
  return pending !== null;
}
